import { useState, useEffect, useRef, ComponentType } from 'react';
import { featureLoader } from '../utils/dynamicFeatureLoader';

interface UseLazyComponentOptions {
  preloadOnHover?: boolean;
  preloadOnVisible?: boolean;
  threshold?: number;
  rootMargin?: string;
}

interface LazyComponentState<T> {
  Component: ComponentType<T> | null;
  loading: boolean;
  error: Error | null;
  preload: () => void;
}

/**
 * Hook for lazy loading components with various trigger options
 */
export function useLazyComponent<T = any>(
  featureName: string,
  options: UseLazyComponentOptions = {}
): LazyComponentState<T> {
  const [Component, setComponent] = useState<ComponentType<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadComponent = async () => {
    if (Component || loading) return;

    setLoading(true);
    setError(null);

    try {
      const module = await featureLoader.loadFeature(featureName);
      setComponent(() => module.default);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const preload = () => {
    if (!Component && !loading) {
      loadComponent();
    }
  };

  // Set up intersection observer for visibility-based loading
  useEffect(() => {
    if (options.preloadOnVisible && elementRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              preload();
              observerRef.current?.disconnect();
            }
          });
        },
        {
          threshold: options.threshold || 0.1,
          rootMargin: options.rootMargin || '50px'
        }
      );

      observerRef.current.observe(elementRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [options.preloadOnVisible, options.threshold, options.rootMargin]);

  // Set up hover-based preloading
  useEffect(() => {
    if (options.preloadOnHover && elementRef.current) {
      const element = elementRef.current;
      
      const handleMouseEnter = () => preload();
      const handleFocus = () => preload();
      
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('focus', handleFocus);
      
      return () => {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('focus', handleFocus);
      };
    }
  }, [options.preloadOnHover]);

  return {
    Component,
    loading,
    error,
    preload
  };
}

/**
 * Hook for lazy loading with intersection observer
 */
export function useLazyComponentOnVisible<T = any>(
  featureName: string,
  threshold: number = 0.1,
  rootMargin: string = '50px'
): LazyComponentState<T> & { ref: (element: HTMLElement | null) => void } {
  const [Component, setComponent] = useState<ComponentType<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);

  const loadComponent = async () => {
    if (Component || loading) return;

    setLoading(true);
    setError(null);

    try {
      const module = await featureLoader.loadFeature(featureName);
      setComponent(() => module.default);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const preload = () => {
    if (!Component && !loading) {
      loadComponent();
    }
  };

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            preload();
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, threshold, rootMargin]);

  return {
    Component,
    loading,
    error,
    preload,
    ref: setElement
  };
}

/**
 * Hook for lazy loading charts and data visualizations
 */
export function useLazyChart(
  chartType: 'line' | 'bar' | 'pie' | 'scatter' | 'heatmap',
  data?: any
) {
  const [ChartComponent, setChartComponent] = useState<ComponentType<any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!data) return; // Don't load chart until we have data

    const loadChart = async () => {
      setLoading(true);
      setError(null);

      try {
        let module: { default: ComponentType<any> };
        switch (chartType) {
          case 'line':
            module = await import('../components/charts/LineChart');
            break;
          case 'bar':
            module = await import('../components/charts/BarChart');
            break;
          case 'pie':
            module = await import('../components/charts/PieChart');
            break;
          case 'scatter':
            module = await import('../components/charts/ScatterChart');
            break;
          case 'heatmap':
            module = await import('../components/charts/HeatmapChart');
            break;
          default:
            throw new Error(`Unknown chart type: ${chartType}`);
        }
        
        setChartComponent(() => module.default);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadChart();
  }, [chartType, data]);

  return { ChartComponent, loading, error };
}

/**
 * Hook for lazy loading based on user interaction
 */
export function useLazyComponentOnInteraction<T = any>(
  featureName: string,
  triggerEvents: string[] = ['click', 'mouseenter', 'focus']
): LazyComponentState<T> & { 
  ref: (element: HTMLElement | null) => void;
  triggerLoad: () => void;
} {
  const [Component, setComponent] = useState<ComponentType<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);

  const loadComponent = async () => {
    if (Component || loading) return;

    setLoading(true);
    setError(null);

    try {
      const module = await featureLoader.loadFeature(featureName);
      setComponent(() => module.default);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const triggerLoad = () => {
    loadComponent();
  };

  useEffect(() => {
    if (!element) return;

    const handlers = triggerEvents.map(eventType => {
      const handler = () => loadComponent();
      element.addEventListener(eventType, handler, { once: true });
      return { eventType, handler };
    });

    return () => {
      handlers.forEach(({ eventType, handler }) => {
        element.removeEventListener(eventType, handler);
      });
    };
  }, [element, triggerEvents]);

  return {
    Component,
    loading,
    error,
    preload: loadComponent,
    ref: setElement,
    triggerLoad
  };
}